import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as shiftService from '../../services/shiftService';

export const fetchTodayStatus = createAsyncThunk(
  'shift/fetchTodayStatus',
  async (_, { rejectWithValue }) => {
    try {
      const res = await shiftService.getTodayStatus();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch session status');
    }
  }
);

export const closeDay = createAsyncThunk(
  'shift/closeDay',
  async (_, { rejectWithValue }) => {
    try {
      const res = await shiftService.closeDaySession();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.error?.message || 'Failed to close session'
      );
    }
  }
);

export const startNewDay = createAsyncThunk(
  'shift/startNewDay',
  async (_, { rejectWithValue }) => {
    try {
      const res = await shiftService.startNewDaySession();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.error?.message || 'Failed to start a new day'
      );
    }
  }
);

export const fetchClosedDays = createAsyncThunk(
  'shift/fetchClosedDays',
  async (params, { rejectWithValue }) => {
    try {
      const res = await shiftService.getClosedDays(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch closed sessions');
    }
  }
);

export const fetchShifts = createAsyncThunk(
  'shift/fetchShifts',
  async (params, { rejectWithValue }) => {
    try {
      const res = await shiftService.getShifts(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch shifts');
    }
  }
);

const initialState = {
  status: 'NO_DAY',
  businessDate: null,
  dayId: null,
  summary: null,
  currentUser: null,
  closedDays: [],
  shifts: [],
  loading: false,
  closing: false,
  starting: false,
  error: null,
};

const shiftSlice = createSlice({
  name: 'shift',
  initialState,
  reducers: {
    clearShiftState: (state) => {
      state.status = 'NO_DAY';
      state.businessDate = null;
      state.dayId = null;
      state.summary = null;
      state.currentUser = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTodayStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodayStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        state.businessDate = action.payload.businessDate;
        state.dayId = action.payload.dayId || null;
        state.summary = action.payload.summary || null;
        state.currentUser = action.payload.currentUser || null;
      })
      .addCase(fetchTodayStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(closeDay.pending, (state) => {
        state.closing = true;
        state.error = null;
      })
      .addCase(closeDay.fulfilled, (state, action) => {
        state.closing = false;
        const payload = action.payload;
        const day = payload.businessDay || {};
        const summary = payload.summary || {};
        state.status = day.status || 'CLOSED';
        state.dayId = day.dayId || state.dayId;
        state.summary = summary;
        state.currentUser = null;
      })
      .addCase(closeDay.rejected, (state, action) => {
        state.closing = false;
        state.error = action.payload;
      })
      .addCase(startNewDay.pending, (state) => {
        state.starting = true;
        state.error = null;
      })
      .addCase(startNewDay.fulfilled, (state, action) => {
        state.starting = false;
        const day = action.payload.businessDay || {};
        state.status = day.status || 'OPEN';
        state.businessDate = day.businessDate || state.businessDate;
        state.dayId = day.dayId || state.dayId;
        state.summary = { ...state.summary, ...(action.payload.summary || {}) };
        state.currentUser = action.payload.currentUser || null;
      })
      .addCase(startNewDay.rejected, (state, action) => {
        state.starting = false;
        state.error = action.payload;
      })
      .addCase(fetchClosedDays.fulfilled, (state, action) => {
        state.closedDays = action.payload.items || action.payload.days || action.payload || [];
      })
      .addCase(fetchClosedDays.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.shifts = action.payload.items || action.payload.shifts || action.payload || [];
      })
      .addCase(fetchShifts.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearShiftState, clearError } = shiftSlice.actions;
export default shiftSlice.reducer;